import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "swiper/css/scrollbar";
import "../../theme/SwiperOverrides.css";

import * as styles from "./QuickLinksEmbedded.module.scss";

import React, { useRef } from "react";
import { A11y, Navigation, Scrollbar, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { navigate } from "gatsby";
import { useClientSide } from "../../hooks/useClientSide";
import { useIsTouchDevice } from "../../hooks/useIsTouchDevice";
import { Colours } from "../../utils/colours";
import { pushToDataLayer } from "../../utils/google";
import { SearchInput, type SearchInputProps } from "../SearchInput/SearchInput";
import ProcessedImage from "../common/ProcessedImage/ProcessedImage";
import type { QuickLinksItemProps } from "../common/QuickLinksItem";

const QuickLinksEmbeddedItem: React.FC<QuickLinksItemProps> = ({
	title,
	imgSrc,
	imgAlt,
	url,
}) => {
	return (
		<div className={styles.embeddedquicklinkitem}>
			<button
				type="button"
				className={styles.iconwrapper}
				tabIndex={0}
				onClick={(e) => {
					e.preventDefault();
					pushToDataLayer({
						event: "button_navigation",
						button_clicked: title,
						button_url: url,
						button_location: window.location.href,
					});
					navigate(url);
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter") navigate(url);
				}}
			>
				<ProcessedImage
					src={imgSrc}
					dimensions="80x80"
					width={80}
					height={80}
					alt={imgAlt}
				/>
			</button>
			<h2 className={styles.quicklinkstitle}>{title}</h2>
		</div>
	);
};

export interface QuickLinksEmbeddedProps {
	title: string;
	items: QuickLinksItemProps[];
	withSearch?: SearchInputProps;
	theme?: "primary" | "alternative";
}

export const QuickLinksEmbedded: React.FC<QuickLinksEmbeddedProps> = ({
	title,
	items,
	withSearch,
	theme = "alternative",
}) => {
	const parentRef = useRef();
	const isClientSide = useClientSide();
	const touchDevice = useIsTouchDevice();
	return (
		<div
			ref={parentRef}
			className={styles.quicklinksinner}
			style={{
				backgroundColor:
					theme === "primary" ? Colours.SUNSHINE_YELLOW : Colours.SANDY_FUR,
			}}
		>
			{!!withSearch && <SearchInput {...withSearch} />}
			{!!title && <h2 className={styles.quicklinkssectiontitle}>{title}</h2>}
			<div className={styles.swiperwrapper}>
				{isClientSide && (
					<Swiper
						className={styles.swiper}
						centerInsufficientSlides={true}
						modules={[Virtual, Navigation, A11y, Scrollbar]}
						edgeSwipeThreshold={10}
						threshold={0}
						grabCursor={true}
						longSwipes={true}
						resistance={false}
						shortSwipes={!!touchDevice}
						loop={false}
						centeredSlides={false}
						scrollbar={{ draggable: true }}
						noSwiping={items.length <= 3}
						initialSlide={0}
						breakpoints={{
							0: {
								slidesPerView: 2.5,
								enabled: items.length >= 3,
								spaceBetween: 25,
								autoplay: {
									delay: 1500,
								},
								centeredSlides: false,
							},
							414: {
								slidesPerView: 3.5,
								enabled: items.length >= 4,
								spaceBetween: 25,
								autoplay: {
									delay: 1500,
								},
								centeredSlides: false,
							},
							600: {
								slidesPerView: Math.min(items.length, 7),
								enabled: items.length >= 7,
								spaceBetween: 25,
								autoplay: {
									delay: 1500,
								},
								centeredSlides: false,
							},
						}}
					>
						{items.map((quickLink) => (
							<SwiperSlide key={quickLink.title}>
								<QuickLinksEmbeddedItem {...quickLink} />
							</SwiperSlide>
						))}
					</Swiper>
				)}
			</div>
		</div>
	);
};

export default QuickLinksEmbedded;
